import { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import Constant from '@/common/constant/Constant';
import CorporationUtils from '@/common/utils/CorporationUtils';
import Logger from '@/common/utils/Logger';
import { clearTranslationMisses, getTranslationMisses } from '@/common/utils/tr';
import translationDb from '@/common/utils/translationDb';
import translationStore from '@/common/utils/translationStore';
import { isTranslationActive, resolveTranslationConfig } from '@/common/utils/translationUtils';
import {
    getBulkTranslationMap,
    MAX_TEXTS_PER_REQUEST,
    translateMissingTexts
} from '@/service/translationService';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { translationReloadDialogAtom, translationVersionAtom } from '@/state/atom/translationAtom';
import { userAtom } from '@/state/atom/userAtom';

/**
 * Auto-translation wiring.
 *
 * useTranslationDictionaryLoader() is mounted once (WrapLayout) and owns the
 * lifecycle across the cache tiers:
 *   translationStore (memory, synchronous - what tr() reads)
 *   translationDb    (IndexedDB, durable across reloads)
 *   AppSync          (source of truth for UI/FIELD_LABEL/TAG)
 *
 * Routing differs per usage, and the difference is the whole point:
 *   UI / FIELD_LABEL - bounded, bulk-loaded at startup, delta-synced afterwards.
 *   TAG              - a production org has 500k tags, so nothing is bulk-loaded;
 *                      misses are resolved lazily from IndexedDB, then the API.
 *   ONDEMAND         - not handled here at all; see useContentTranslation().
 */
const TAG_NAME = 'useTranslation';

const { UI, FIELD_LABEL, TAG } = Constant.TRANSLATION_USAGE;

// How often the MISS batch is flushed. Deliberately not per-render.
const FLUSH_INTERVAL_MS = 3000;

// Session-wide ceiling so a pathological page cannot spam the translate API.
const MAX_REQUESTED_PER_SESSION = 2000;

/**
 * "org user" pairs whose translationCacheResetAt was already checked in this page
 * load. Module state on purpose: the check runs only on access / F5, never while the
 * user works - a wipe mid-session would flash every tr() string back to the source
 * text until the full load finished. Survives the loader remounting across layouts.
 */
const resetCheckedSet = new Set();

/**
 * Subscribes the calling component to the translation cache so it re-renders once
 * the dictionary is loaded or extended. Call it next to the other Recoil hooks;
 * the tr() call sites themselves need no change.
 */
export function useTranslationSubscription() {
    useRecoilValue(translationVersionAtom);
}

/**
 * Loads the bulk dictionaries and keeps them up to date. Mount exactly once,
 * above every component that calls tr() (currently WrapLayout).
 */
export function useTranslationDictionaryLoader() {
    const corporationDetail = useRecoilValue(corporationAtom);
    const userDetail = useRecoilValue(userAtom);
    const setTranslationVersion = useSetRecoilState(translationVersionAtom);
    const setReloadDialog = useSetRecoilState(translationReloadDialogAtom);

    const affiliationOrganization = corporationDetail?.id;
    // Org that owns the dictionary: a guest / partner org shares its inviter's.
    // Used for every dictionary read and every IndexedDB scope; the Lambda receives
    // affiliationOrganization and resolves the same org on its side.
    const dictionaryOrgId = CorporationUtils.resolveDictionaryOrgId(corporationDetail);
    const userId = userDetail?.id;

    // A ref so a flush already in flight is visible to the next interval tick.
    const isFlushingRef = useRef(false);

    // Remembers the last (org, user, effective display language) so a change to
    // the language while the page is open can be told apart from the first mount,
    // a login, or an organization switch. Only the first case prompts a reload.
    const displayStateRef = useRef(null);

    useEffect(() => {
        if (!affiliationOrganization || !dictionaryOrgId || !userId) {
            // Logged out (signOut resets userAtom without a page reload): the next
            // login counts as a new access, so its reset check must run again.
            resetCheckedSet.clear();
            return;
        }

        // Turn every store mutation into a Recoil version bump -> re-render.
        translationStore.setNotifier(() => setTranslationVersion(translationStore.getVersion()));

        const config = resolveTranslationConfig(corporationDetail, userDetail);
        translationStore.setConfig({ ...config, dictionaryOrgId });

        // The language the UI actually renders in: the main language while
        // translation is active, otherwise the source language (original text).
        const effectiveLanguage = isTranslationActive(config)
            ? config.mainLanguage
            : config.sourceLanguage;
        const previous = displayStateRef.current;
        displayStateRef.current = { affiliationOrganization, userId, effectiveLanguage };

        // Prompt a reload only for a real in-session language change: same org and
        // user, but the effective display language differs. The first mount
        // (previous === null), a login, or an org switch must never prompt.
        if (
            previous &&
            previous.affiliationOrganization === affiliationOrganization &&
            previous.userId === userId &&
            previous.effectiveLanguage !== effectiveLanguage
        ) {
            setReloadDialog(true);
        }

        // Multilingual OFF, or the user reads the source language: no API calls,
        // no IndexedDB access at all.
        if (!isTranslationActive(config)) {
            translationStore.clearEntries();
            return () => translationStore.setNotifier(null);
        }

        let isCancelled = false;
        const { mainLanguage } = config;

        /** Stores translations in memory + IndexedDB, capping the scope if needed. */
        const persist = async (usage, pairs) => {
            if (!pairs || Object.keys(pairs).length === 0) return;

            translationStore.putMany(usage, pairs);
            await translationDb.putScopeEntries(
                dictionaryOrgId,
                usage,
                mainLanguage,
                pairs
            );

            const limit = Constant.TRANSLATION_CACHE_LIMIT[usage];
            if (limit) {
                await translationDb.enforceScopeLimit(
                    dictionaryOrgId,
                    usage,
                    mainLanguage,
                    limit
                );
            }
        };

        /**
         * Hydrate from IndexedDB, then sync the delta from AppSync.
         *
         * The next high-water mark is the newest server updatedAt seen, minus an
         * overlap - never the client clock, which may be skewed. The overlap also
         * re-reads rows that reached the GSI late; re-storing them is harmless.
         */
        const bootstrapUsage = async usage => {
            const rows = await translationDb.readScope(
                dictionaryOrgId,
                usage,
                mainLanguage
            );
            if (isCancelled) return;
            translationStore.loadRows(rows);

            const meta = await translationDb.readMeta(dictionaryOrgId, usage, mainLanguage);
            if (isCancelled) return;

            const result = await getBulkTranslationMap(
                dictionaryOrgId,
                usage,
                mainLanguage,
                meta?.syncedAt || null
            );

            if (isCancelled) return;
            // null means the sync failed - keep the cache we hydrated from.
            if (result === null) return;

            await persist(usage, result.map);
            if (isCancelled) return;

            // Nothing new: keep the previous mark rather than advancing it.
            const syncedAt = result.maxUpdatedAt
                ? new Date(Date.parse(result.maxUpdatedAt) - Constant.TRANSLATION_SYNC_OVERLAP_MS).toISOString()
                : meta?.syncedAt || null;

            await translationDb.writeMeta(dictionaryOrgId, usage, mainLanguage, {
                syncedAt,
                entryCount: translationStore.size()
            });
        };

        const bootstrap = async () => {
            // Keep the database from growing without bound for org switchers.
            translationDb.deleteOtherOrganizations(dictionaryOrgId);

            // Deleted dictionary rows never show up in a delta sync, so the server bumps
            // Corporation.translationCacheResetAt instead. Any change -> drop the whole
            // org cache (every usage and language) and start over with a full load.
            // No marker yet also wipes, once: that cache predates the marker.
            // A guest / partner org checks its inviter's value (the dictionary owner).
            const resetCheckKey = `${dictionaryOrgId} ${userId}`;
            if (!resetCheckedSet.has(resetCheckKey)) {
                const serverResetAt = CorporationUtils.getDictionaryCacheResetAt(corporationDetail);
                const marker = await translationDb.readCacheResetMarker(dictionaryOrgId);
                if (isCancelled) return;
                if (!marker || marker.resetAt !== serverResetAt) {
                    await translationDb.deleteOrganization(dictionaryOrgId);
                    translationStore.clearEntries();
                    await translationDb.writeCacheResetMarker(dictionaryOrgId, serverResetAt);
                    if (isCancelled) return;
                }
                // Marked only once the check completed, so a cancelled run checks again.
                resetCheckedSet.add(resetCheckKey);
            }

            // TAG is absent from this list on purpose - see the module comment.
            for (const usage of Constant.TRANSLATION_BULK_USAGE) {
                await bootstrapUsage(usage);
                if (isCancelled) return;
            }
        };

        bootstrap();

        /**
         * Resolves one usage's misses. TAG consults IndexedDB first because it is
         * never bulk-loaded, so a miss is usually just "not in memory yet" rather
         * than "not translated anywhere".
         */
        const resolveMisses = async (usage, texts) => {
            let pending = texts;

            if (usage === TAG) {
                const cached = await translationDb.getMany(
                    dictionaryOrgId,
                    usage,
                    mainLanguage,
                    pending
                );
                if (Object.keys(cached).length > 0) {
                    translationStore.putMany(usage, cached);
                    pending = pending.filter(text => !cached[text]);
                }
            }

            if (pending.length === 0 || isCancelled) return;

            const translated = await translateMissingTexts(
                affiliationOrganization,
                usage,
                mainLanguage,
                pending,
                userId
            );
            if (!isCancelled) {
                await persist(usage, translated);
            }
        };

        const flushMisses = async () => {
            if (isFlushingRef.current || isCancelled) return;

            const pending = getTranslationMisses().filter(
                ({ usage, sourceText }) => !translationStore.isRequested(usage, sourceText)
            );
            clearTranslationMisses();

            if (pending.length === 0) return;
            if (translationStore.requestedCount() >= MAX_REQUESTED_PER_SESSION) {
                Logger.error(
                    `${TAG_NAME} flushMisses`,
                    `translation request cap reached (${MAX_REQUESTED_PER_SESSION}), dropping ${pending.length} string(s)`
                );
                return;
            }

            // Group by usage so each batch goes down the right path.
            const byUsage = {};
            pending.slice(0, MAX_TEXTS_PER_REQUEST).forEach(({ usage, sourceText }) => {
                if (!byUsage[usage]) byUsage[usage] = [];
                byUsage[usage].push(sourceText);
                // Mark before awaiting: a failed batch must not be retried forever.
                translationStore.markRequested(usage, sourceText);
            });

            isFlushingRef.current = true;
            try {
                for (const usage of Object.keys(byUsage)) {
                    if (usage !== UI && usage !== FIELD_LABEL && usage !== TAG) continue;
                    await resolveMisses(usage, byUsage[usage]);
                    if (isCancelled) return;
                }
            } finally {
                isFlushingRef.current = false;
            }
        };

        const intervalId = setInterval(flushMisses, FLUSH_INTERVAL_MS);

        return () => {
            isCancelled = true;
            clearInterval(intervalId);
            clearTranslationMisses();
            translationStore.setNotifier(null);
        };
        // corporationDetail/userDetail are only read for their config values; the
        // ids are what decide whether the dictionary has to be reloaded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        affiliationOrganization,
        dictionaryOrgId,
        userId,
        corporationDetail?.contractType,
        corporationDetail?.inviterSetting,
        corporationDetail?.limitValue?.multiLanguage,
        corporationDetail?.limitValue?.usingMultiLanguage,
        userDetail?.userSetting
    ]);
}

export default useTranslationSubscription;
